import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { CheckBox, Icon, IconButton } from '@st-operations/uikit/src/v2/index.js'

import { uploadFile } from '_services/data-explorer'

import { hasHeaderNamed } from './header.js'

import './styles.scss'
import { logErrorAsync } from '../../../../../../lib/utility'

// only text formats survive the trip: the API parses the file as a CSV
const SUPPORTED_EXTENSIONS = ['csv', 'tsv', 'txt']

// only the first row is inspected, and these files run to millions of rows
const HEADER_SAMPLE_BYTES = 64 * 1024

const readFirstRows = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => resolve(event.target.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file.slice(0, HEADER_SAMPLE_BYTES))
  })

const AddFiltersModalFileInput = ({
  label,
  onAddFile,
  fileName,
  headerValue,
  hasHeaderRow,
  onHasHeaderRowChange,
}) => {
  const [dragging, setDragging] = useState(false)
  const [dragCounter, setDragCounter] = useState(0)
  const [file, setFile] = useState(fileName ? { name: fileName } : null)
  const [isLoading, setIsLoading] = useState(null)
  // Toggling "has header row" re-uploads the file, so it can only be changed
  // while we still hold the bytes. A reopened filter only has the saved path.
  const [uploadedFile, setUploadedFile] = useState(null)

  const dropRef = useRef(null)

  const handleDrag = (event) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDragIn = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setDragCounter((prev) => prev + 1)
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      setDragging(true)
    }
  }

  const handleDragOut = () => {
    setDragCounter((prev) => prev - 1)
  }

  useEffect(() => {
    if (dragCounter === 0) {
      setDragging(false)
    }
  }, [dragCounter])

  const uploadWithHeaderRow = async (fileToUpload, hasHeader) => {
    setIsLoading(true)

    try {
      const formData = new FormData()
      formData.append('file', fileToUpload, fileToUpload.name)
      // tell the API which column to key the stored file on, so it always ends
      // up in the shape the filters read
      formData.append('column', headerValue)
      formData.append('has_header', hasHeader)

      const response = await uploadFile(formData)

      setFile(fileToUpload)
      setUploadedFile(fileToUpload)
      onAddFile(response.path, { hasHeader })
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message || 'There was an error uploading your file')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddFile = async (fileToAdd) => {
    const extension = fileToAdd?.name?.split('.').splice(-1)[0]?.toLowerCase()
    if (!SUPPORTED_EXTENSIONS.includes(extension)) {
      toast.error('The extension you tried to upload is not supported')
      return
    }

    let contents
    try {
      contents = await readFirstRows(fileToAdd)
    } catch (error) {
      logErrorAsync(error)
      toast.error('There was an error reading your file')
      return
    }

    // The file itself is better evidence than the checkbox, so detect the header
    // instead of rejecting the upload — and say so when the two disagree.
    const detected = hasHeaderNamed(contents, headerValue)
    if (detected !== hasHeaderRow) {
      toast(
        detected
          ? `Found a "${headerValue}" header row — it will not be used as a value.`
          : `No "${headerValue}" header row found — every row will be used as a value.`
      )
    }

    await uploadWithHeaderRow(fileToAdd, detected)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setDragging(false)

    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      handleAddFile(event.dataTransfer.files[0])
      event.dataTransfer.clearData()
      setDragCounter(0)
    }
  }

  const handleRemoveFile = () => {
    setFile(null)
    setUploadedFile(null)
  }

  const handleFileChoose = (event) => {
    handleAddFile(event.target.files[0])
  }

  const handleHasHeaderRowChange = (event) => {
    const { checked } = event.target

    // the stored file is rewritten around this choice, so changing it after the
    // fact means sending the same file up again
    if (uploadedFile) {
      uploadWithHeaderRow(uploadedFile, checked)
      return
    }

    onHasHeaderRowChange(checked)
  }

  useEffect(() => {
    const body = document.querySelector('body')

    if (isLoading) {
      body.style.cursor = 'wait'
    } else {
      body.style.cursor = null
    }
  }, [isLoading])

  return (
    <div className="add-filters-modal-file-input">
      <p className="add-filters-modal-file-input__text">{label}</p>
      <Choose>
        <When condition={file}>
          <div className="add-filters-modal-file-input__file-container">
            <div className="add-filters-modal-file-input__file">
              <Icon icon="filecsv" type="fill" color="#13682B" width="48px" height="48px" />
              <div>
                <p className="add-filters-modal-file-input__file-title">{file.name}</p>
                <If condition={file.size}>
                  <p className="add-filters-modal-file-input__file-size">{file.size / 100} KB</p>
                </If>
              </div>
            </div>
            <IconButton
              onClick={handleRemoveFile}
              className="add-filters-modal-file-input__delete"
              iconProps={{ icon: 'delete', type: 'fill', color: '#3d3d3d', width: 16, height: 16 }}
            />
          </div>
        </When>
        <Otherwise>
          <label
            onDragEnter={handleDragIn}
            onDragLeave={handleDragOut}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`add-filters-modal-file-input__label ${dragging ? 'active' : ''}`}
            ref={dropRef}
          >
            <Icon icon="download" type="fill" color="#13682B" width="64px" height="64px" />
            <div>
              <p className="add-filters-modal-file-input__title">
                <b>Choose a file</b> or drag it here
              </p>
              <p className="add-filters-modal-file-input__description">
                Make sure your file is in .CSV format
              </p>
              <input
                key={Math.random()}
                accept=".csv,.tsv,.txt"
                onChange={handleFileChoose}
                type="file"
              />
            </div>
          </label>
        </Otherwise>
      </Choose>
      <div className="add-filters-modal-file-input__has-header-container">
        <p className="add-filters-modal-file-input__has-header-label">Has header row</p>
        <CheckBox
          className="add-filters-modal-file-input__has-header-checkbox"
          checked={!!hasHeaderRow}
          disabled={isLoading || (!!file && !uploadedFile)}
          onChange={handleHasHeaderRowChange}
        />
      </div>
      <p className="add-filters-modal-file-input__has-header-hint">
        <Choose>
          <When condition={hasHeaderRow}>
            The first row is a header named <b>{headerValue}</b> and is not used as a value.
          </When>
          <Otherwise>
            Every row is used as a value. A first row named <b>{headerValue}</b> is detected
            automatically on upload.
          </Otherwise>
        </Choose>
      </p>
    </div>
  )
}

AddFiltersModalFileInput.propTypes = {
  onAddFile: PropTypes.func,
  onHasHeaderRowChange: PropTypes.func,
  label: PropTypes.string,
  fileName: PropTypes.string,
  headerValue: PropTypes.string,
  hasHeaderRow: PropTypes.bool,
}

AddFiltersModalFileInput.defaultProps = {
  onAddFile: () => {},
  onHasHeaderRowChange: () => {},
  label: '',
  fileName: '',
  headerValue: '',
  hasHeaderRow: true,
}

export default React.memo(AddFiltersModalFileInput)
